// Keep edge-function requests comfortably below Supabase's 150s request-idle
// cap. Full protocols get a larger focused payload; auxiliary manuals (IP/lab/
// pharmacy/IB/ICF/budget/other) are deliberately tighter because they were the
// observed timeout class and rarely contain the full SoA + endpoint universe.
const MAX_PROTOCOL_AI_TEXT_CHARS = 140_000;
const MAX_AUXILIARY_AI_TEXT_CHARS = 70_000;

const SECTION_MARKERS = [
  "protocol synopsis",
  "study synopsis",
  "study design",
  "study rationale",
  "study objectives",
  "objectives and endpoints",
  "primary objective",
  "secondary objective",
  "exploratory objective",
  "primary endpoint",
  "secondary endpoint",
  "exploratory endpoint",
  "number of participants",
  "estimated enrollment",
  "sample size",
  "inclusion criteria",
  "exclusion criteria",
  "eligibility criteria",
  "schedule of activities",
  "schedule of assessments",
  "schedule of events",
  "visit schedule",
  "study visits",
  "study intervention",
  "investigational product",
  "dose regimen",
  "dosing schedule",
  "vaccination",
  "randomization",
  "randomisation",
  "safety monitoring",
  "adverse event",
  "stopping rules",
  "study arms",
  "treatment arms",
  "budget",
  "cost schedule",
  "financial agreement",
  "payment schedule",
  "per patient cost",
  "per-patient cost",
  "site fee",
  "screen failure fee",
  "startup fee",
  "overhead",
  "participant reimbursement",
  "participant stipend",
  "subject stipend",
  "travel reimbursement",
  "reimbursement schedule",
  "stipend",
];

function normalizeDocumentText(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .replace(/[\t ]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function isAuxiliaryDocument(filename?: string): boolean {
  const name = (filename || "").toLowerCase().replace(/\+/g, " ");
  return /\b(ip|pharmacy|lab|laboratory|manual|brochure|ib|icf|consent|budget|cost|financial)\b/.test(name);
}

export function buildTrialExtractionPayload(text: string, filename?: string): string {
  const cleaned = normalizeDocumentText(text);
  const maxChars = isAuxiliaryDocument(filename) ? MAX_AUXILIARY_AI_TEXT_CHARS : MAX_PROTOCOL_AI_TEXT_CHARS;
  if (cleaned.length <= maxChars) return cleaned;

  const lower = cleaned.toLowerCase();
  // Always keep the title page and the tail (often appendices / SoA tables).
  const ranges: Array<[number, number]> = [[0, isAuxiliaryDocument(filename) ? 25_000 : 35_000]];
  const markerWindow = isAuxiliaryDocument(filename) ? 14_000 : 22_000;

  for (const marker of SECTION_MARKERS) {
    let from = 0;
    // Capture every occurrence of the marker (SoA + I/E can appear multiple times).
    while (true) {
      const idx = lower.indexOf(marker, from);
      if (idx < 0) break;
      ranges.push([Math.max(0, idx - 2_500), Math.min(cleaned.length, idx + markerWindow)]);
      from = idx + marker.length;
    }
  }

  ranges.push([Math.max(0, cleaned.length - (isAuxiliaryDocument(filename) ? 6_000 : 9_000)), cleaned.length]);

  const merged = ranges
    .sort((a, b) => a[0] - b[0])
    .reduce<Array<[number, number]>>((acc, [start, end]) => {
      const last = acc[acc.length - 1];
      if (last && start <= last[1] + 2_000) {
        last[1] = Math.max(last[1], end);
      } else {
        acc.push([start, end]);
      }
      return acc;
    }, []);

  return merged
    .map(([start, end], idx) => `--- DOCUMENT EXCERPT ${idx + 1} (chars ${start}–${end}) ---\n${cleaned.slice(start, end)}`)
    .join("\n\n")
    .slice(0, maxChars);
}

export async function getFunctionErrorMessage(error: unknown, fallback = "Edge Function failed"): Promise<string> {
  const err = error as { message?: string; context?: Response } | null;
  const context = err?.context;

  if (context) {
    try {
      const copy = context.clone();
      const body = await copy.json();
      if (body?.error) return String(body.error);
      if (body?.message) return String(body.message);
    } catch {
      try {
        const text = await context.clone().text();
        if (text) return text.slice(0, 500);
      } catch {
        // fall through to message
      }
    }
  }

  return err?.message || fallback;
}